import { PlusIcon } from '@heroicons/react/20/solid';

import { useAuth } from '@context';
import { InboundProcess, OutboundProcess } from '@features/supplyChain/types';
import {
  useAuthoriseForPicking,
  useGoodsReceivedNote,
  useInboundProcess,
  useOutboundProcess,
  useOutboundShipmentNote,
  usePickingNote,
  useProofOfDelivery,
  useShipmentFilter,
  useShipments,
} from '@hooks';
import { UserRoleService } from '@services';
import { SupplyChainProcessDrawers } from '@views/Noticeboard/components/SupplyChainProcessDrawers';
import { SupplyChainProcessModal } from '@views/Noticeboard/components/SupplyChainProcessModal';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery, useReadLocalStorage } from 'usehooks-ts';
import {
  businessProfileAtom,
  isInboundSidebarOpenAtom,
  isOutboundSidebarOpenAtom,
  selectedInboundProcessIdAtom,
  selectedOutboundProcessIdAtom,
  selectedShipmentNoteNumberAtom,
} from '../../store/jotai';
import { Button } from '../../tailwindComponents/Button';
import { Checkbox } from '../../tailwindComponents/Checkbox/Checkbox';
import { Datepicker } from '../../tailwindComponents/Datepicker/Datepicker';
import { Input } from '../../tailwindComponents/Input/Input';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Pagination } from '../../tailwindComponents/Pagination/Pagination';
import { InboundShipmentsTable, OutboundShipmentsTable } from './components';
import { InboundSortField, OutboundSortField, SortDirection } from './types';
import {
  despatchLineCount,
  despatchOrderedQuantity,
  hasDespatchOrShipmentNote,
} from '../../utils/despatch';

dayjs.extend(advancedFormat);

const ITEMS_PER_PAGE_OPTIONS = [8, 16, 24, 50];

export function Shipments() {
  const isDesktop = useMediaQuery('(min-width: 62em)');
  const isTablet = useMediaQuery('(min-width: 48em)');
  const darkMode = useReadLocalStorage('usehooks-ts-dark-mode');
  const { user } = useAuth();
  const { isWarehouseClient, isWarehouseManager, isWarehouseOperator } =
    UserRoleService();
  const isLsp = isWarehouseManager(user) || isWarehouseOperator(user);
  const businessProfile = useAtomValue(businessProfileAtom);
  const [selectedShipmentNoteNumber, setSelectedShipmentNoteNumber] = useAtom(
    selectedShipmentNoteNumberAtom
  );
  const [isShipmentNoteModalOpen, setIsShipmentNoteModalOpen] = useState(false);
  const [previewedInboundProcess, setPreviewedInboundProcess] = useState<
    InboundProcess | undefined
  >(undefined);
  const [previewedOutboundProcess, setPreviewedOutboundProcess] = useState<
    OutboundProcess | undefined
  >(undefined);
  const navigate = useNavigate();

  const {
    query,
    searchFilter,
    handleChangeSearchFilter,
    shipmentType,
    setShipmentType,
    dateFrom,
    handleChangeDateFrom,
    dateTo,
    handleChangeDateTo,
    exceptionsOnly,
    setExceptionsOnly,
  } = useShipmentFilter();

  const {
    inboundShipments,
    fetchInboundShipments,
    outboundShipments,
    fetchOutboundShipments,
  } = useShipments();

  const inboundProcessState = useInboundProcess();
  const { inboundProcesses, setSelectedInboundProcess } = inboundProcessState;

  const outboundProcessState = useOutboundProcess();
  const { outboundProcesses, setSelectedOutboundProcess } =
    outboundProcessState;

  const goodsReceivedNoteState = useGoodsReceivedNote();
  const authoriseForPickingState = useAuthoriseForPicking();
  const pickingNoteState = usePickingNote();
  const outboundShipmentNoteState = useOutboundShipmentNote();
  const proofOfDeliveryState = useProofOfDelivery();

  const [isInboundSidebarOpen, setIsInboundSidebarOpen] = useAtom(
    isInboundSidebarOpenAtom
  );
  const [isOutboundSidebarOpen, setIsOutboundSidebarOpen] = useAtom(
    isOutboundSidebarOpenAtom
  );
  const setSelectedInboundProcessId = useSetAtom(selectedInboundProcessIdAtom);
  const setSelectedOutboundProcessId = useSetAtom(
    selectedOutboundProcessIdAtom
  );

  const handleOpenInboundProcess = (inboundProcess: InboundProcess) => {
    setSelectedInboundProcess(inboundProcess);
    setSelectedInboundProcessId(inboundProcess.id);
    setIsInboundSidebarOpen(true);
    if (isOutboundSidebarOpen) {
      setIsOutboundSidebarOpen(false);
    }
  };

  const handleOpenOutboundProcess = (outboundProcess: OutboundProcess) => {
    setSelectedOutboundProcess(outboundProcess);
    setSelectedOutboundProcessId(outboundProcess.id);
    setIsOutboundSidebarOpen(true);
    if (isInboundSidebarOpen) {
      setIsInboundSidebarOpen(false);
    }
  };

  // Sort, pagination, and selection state
  const [inboundSortField, setInboundSortField] =
    useState<InboundSortField | null>(null);
  const [outboundSortField, setOutboundSortField] =
    useState<OutboundSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filters = useMemo(
    () => ({
      searchFilter,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [searchFilter, dateFrom, dateTo]
  );

  useEffect(() => {
    if (shipmentType === 'Inbound') {
      fetchInboundShipments(filters);
    } else {
      fetchOutboundShipments(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboundProcesses, outboundProcesses, shipmentType, filters]);

  useEffect(() => {
    if (selectedShipmentNoteNumber) {
      setIsShipmentNoteModalOpen(true);
      if (shipmentType === 'Inbound') {
        setPreviewedInboundProcess(
          inboundShipments.find(
            (shipment) =>
              shipment.shipmentNote.shipmentNoteNumber ===
              selectedShipmentNoteNumber
          )
        );
      } else {
        setPreviewedOutboundProcess(
          outboundShipments.find(
            (shipment) =>
              (shipment.shipmentNote?.shipmentNoteNumber ??
                shipment.despatchNote?.despatchNoteNumber) ===
              selectedShipmentNoteNumber
          )
        );
      }
    } else {
      setIsShipmentNoteModalOpen(false);
      if (shipmentType === 'Inbound') {
        setPreviewedInboundProcess(undefined);
      } else {
        setPreviewedOutboundProcess(undefined);
      }
    }
  }, [
    selectedShipmentNoteNumber,
    shipmentType,
    inboundShipments,
    outboundShipments,
  ]);

  useEffect(() => {
    if (!isShipmentNoteModalOpen) {
      setSelectedShipmentNoteNumber('');
    }
  }, [isShipmentNoteModalOpen, setSelectedShipmentNoteNumber]);

  // Reset pagination when filters or type change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [
    shipmentType,
    searchFilter,
    dateFrom,
    dateTo,
    exceptionsOnly,
    itemsPerPage,
  ]);

  // Sorting handlers
  const handleInboundSort = useCallback(
    (field: InboundSortField) => {
      if (inboundSortField === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setInboundSortField(field);
        setSortDirection('asc');
      }
    },
    [inboundSortField]
  );

  const handleOutboundSort = useCallback(
    (field: OutboundSortField) => {
      if (outboundSortField === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setOutboundSortField(field);
        setSortDirection('asc');
      }
    },
    [outboundSortField]
  );

  // Selection handlers
  const handleSelectChange = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Sorted + filtered inbound data
  const sortedInbound = useMemo(() => {
    let result = [...inboundShipments];

    if (exceptionsOnly) {
      result = result.filter((p) => (p.notices?.length ?? 0) > 0);
    }

    if (inboundSortField) {
      result.sort((a, b) => {
        let cmp = 0;
        switch (inboundSortField) {
          case 'orderPool':
            cmp = a.status.localeCompare(b.status);
            break;
          case 'noteId':
            cmp = a.shipmentNote.shipmentNoteNumber.localeCompare(
              b.shipmentNote.shipmentNoteNumber
            );
            break;
          case 'from':
            cmp = a.shipmentNote.supplier.name.localeCompare(
              b.shipmentNote.supplier.name
            );
            break;
          case 'to':
            cmp = (a.shipmentNote.warehouseAddress ?? '').localeCompare(
              b.shipmentNote.warehouseAddress ?? ''
            );
            break;
          case 'skus':
            cmp =
              a.shipmentNote.skus.reduce((s, sk) => s + sk.skuQuantity, 0) -
              b.shipmentNote.skus.reduce((s, sk) => s + sk.skuQuantity, 0);
            break;
          case 'pallets':
            cmp = a.shipmentNote.skus.length - b.shipmentNote.skus.length;
            break;
          default:
            break;
        }
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [inboundShipments, inboundSortField, sortDirection, exceptionsOnly]);

  // Sorted + filtered outbound data
  const sortedOutbound = useMemo(() => {
    let result = outboundShipments.filter(hasDespatchOrShipmentNote);

    if (exceptionsOnly) {
      result = result.filter((p) => (p.notices?.length ?? 0) > 0);
    }

    if (outboundSortField) {
      result.sort((a, b) => {
        let cmp = 0;
        switch (outboundSortField) {
          case 'noteId': {
            const aNote =
              a.shipmentNote?.shipmentNoteNumber ??
              a.despatchNote?.despatchNoteNumber ??
              '';
            const bNote =
              b.shipmentNote?.shipmentNoteNumber ??
              b.despatchNote?.despatchNoteNumber ??
              '';
            cmp = aNote.localeCompare(bNote);
            break;
          }
          case 'to': {
            const aTo =
              a.shipmentNote?.customerName ??
              a.despatchNote?.customer?.name ??
              a.despatchNote?.customerName ??
              '';
            const bTo =
              b.shipmentNote?.customerName ??
              b.despatchNote?.customer?.name ??
              b.despatchNote?.customerName ??
              '';
            cmp = aTo.localeCompare(bTo);
            break;
          }
          case 'created':
            cmp =
              new Date(a.shipmentNote?.createdAt ?? 0).getTime() -
              new Date(b.shipmentNote?.createdAt ?? 0).getTime();
            break;
          case 'eta': {
            const aEta = a.shipmentNote?.estimatedTimeOfArrival?.dateTime;
            const bEta = b.shipmentNote?.estimatedTimeOfArrival?.dateTime;
            cmp = new Date(aEta ?? 0).getTime() - new Date(bEta ?? 0).getTime();
            break;
          }
          case 'skus': {
            const aSkus = a.shipmentNote
              ? a.shipmentNote.skus.reduce((s, sk) => s + sk.skuQuantity, 0)
              : despatchOrderedQuantity(a.despatchNote);
            const bSkus = b.shipmentNote
              ? b.shipmentNote.skus.reduce((s, sk) => s + sk.skuQuantity, 0)
              : despatchOrderedQuantity(b.despatchNote);
            cmp = aSkus - bSkus;
            break;
          }
          case 'pallets': {
            const aPallets = a.shipmentNote
              ? a.shipmentNote.skus.length
              : despatchLineCount(a.despatchNote);
            const bPallets = b.shipmentNote
              ? b.shipmentNote.skus.length
              : despatchLineCount(b.despatchNote);
            cmp = aPallets - bPallets;
            break;
          }
          case 'carrier':
            cmp = 0; // Carrier is currently hardcoded
            break;
          default:
            break;
        }
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [outboundShipments, outboundSortField, sortDirection, exceptionsOnly]);

  // Paginated data
  const currentData =
    shipmentType === 'Inbound' ? sortedInbound : sortedOutbound;
  const totalItems = currentData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedInbound = sortedInbound.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const paginatedOutbound = sortedOutbound.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAllInbound = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(new Set(paginatedInbound.map((p) => p.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [paginatedInbound]
  );

  const handleSelectAllOutbound = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(new Set(paginatedOutbound.map((p) => p.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [paginatedOutbound]
  );

  const isAllowedToCreateDespatch =
    businessProfile?.hasConnectedBankAccount ||
    businessProfile?.areFinancialFeaturesDisabled;
  const isCreateButtonEnabled =
    shipmentType === 'Inbound' || isAllowedToCreateDespatch;

  return (
    <>
      <PageHeader
        header='Shipments'
        pages={[
          { name: 'Noticeboard', href: '/noticeboard', current: false },
          { name: 'Shipments', href: '/shipments', current: true },
        ]}
      >
        {isWarehouseClient(user) && isCreateButtonEnabled && (
          <Button
            label={
              shipmentType === 'Inbound' ? 'Create Shipment' : 'Create Despatch'
            }
            intent='primary'
            size='sm'
            startIcon={<PlusIcon className='h-4 w-4' />}
            onClick={() =>
              navigate(
                shipmentType === 'Inbound'
                  ? '/inbound-shipment/create'
                  : '/despatch-note/create'
              )
            }
          />
        )}
        {isWarehouseClient(user) && !isCreateButtonEnabled && (
          <Button
            label='Create Despatch'
            intent='primary'
            size='sm'
            startIcon={<PlusIcon className='h-4 w-4' />}
            disabled
            title={
              user?.roles.includes('FinanceAdmin')
                ? 'A bank account must be connected before you can create a despatch note. Please navigate to Account Profile to link a bank account.'
                : 'A bank account must be connected before you can create a despatch note. Please contact your finance admin.'
            }
          />
        )}
      </PageHeader>

      <main className='flex flex-col gap-5 p-4 lg:px-10 lg:pb-20 bg-gray-50'>
        {/* Inbound / Outbound Toggle */}
        <div className='inline-flex self-start rounded-lg border border-gray-200 bg-white p-0.5'>
          <button
            type='button'
            onClick={() => setShipmentType('Inbound')}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              shipmentType === 'Inbound'
                ? 'bg-primary text-white'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Inbound
          </button>
          <button
            type='button'
            onClick={() => setShipmentType('Outbound')}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              shipmentType === 'Outbound'
                ? 'bg-primary text-white'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Outbound
          </button>
        </div>

        {/* Filters Row */}
        <div className='flex flex-wrap items-end gap-3'>
          {/* Search */}
          <div className='w-[320px]'>
            <Input
              name='search'
              placeholder='Search...'
              value={query}
              onChange={handleChangeSearchFilter}
              sizeVariant='sm'
            />
          </div>

          {/* Date From */}
          <div className='w-[180px]'>
            <Datepicker
              name='dateFrom'
              label='Date from'
              value={dateFrom}
              onChange={handleChangeDateFrom}
              sizeVariant='sm'
              endDate={dateTo || undefined}
            />
          </div>

          {/* Date To */}
          <div className='w-[180px]'>
            <Datepicker
              name='dateTo'
              label='Date to'
              value={dateTo}
              onChange={handleChangeDateTo}
              sizeVariant='sm'
              startDate={dateFrom || undefined}
            />
          </div>

          {/* Exceptions only */}
          <Checkbox
            label='Action required only'
            checked={exceptionsOnly}
            onChange={(e) => setExceptionsOnly(e.target.checked)}
          />
        </div>

        {/* Shipments Table */}
        <div
          className={`rounded-xl overflow-x-auto relative bg-white ${
            darkMode
              ? 'border border-gray-300'
              : 'shadow-sm border border-gray-200'
          }`}
        >
          {shipmentType === 'Inbound' ? (
            <InboundShipmentsTable
              inboundProcesses={paginatedInbound}
              sortField={inboundSortField}
              sortDirection={sortDirection}
              onSort={handleInboundSort}
              selectedIds={selectedIds}
              onSelectChange={handleSelectChange}
              onSelectAll={handleSelectAllInbound}
              onOpenProcess={handleOpenInboundProcess}
            />
          ) : (
            <OutboundShipmentsTable
              outboundProcesses={paginatedOutbound}
              sortField={outboundSortField}
              sortDirection={sortDirection}
              onSort={handleOutboundSort}
              selectedIds={selectedIds}
              onSelectChange={handleSelectChange}
              onSelectAll={handleSelectAllOutbound}
              onOpenProcess={handleOpenOutboundProcess}
            />
          )}
        </div>

        {/* Pagination */}
        <div className='flex items-center justify-between py-4'>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
          <div className='flex items-center gap-2.5'>
            <span className='text-sm leading-5 text-gray-500'>
              Show per page:
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className='rounded-lg border border-gray-200 bg-white px-3 py-2.5 pr-8 text-base leading-6 text-gray-900 focus:outline-none'
            >
              {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </main>

      {isShipmentNoteModalOpen &&
        (previewedInboundProcess || previewedOutboundProcess) && (
          <SupplyChainProcessModal
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            process={(previewedInboundProcess ?? previewedOutboundProcess)!}
            isOpen={isShipmentNoteModalOpen}
            setIsOpen={(open) => {
              setIsShipmentNoteModalOpen(open);
              if (!open) setSelectedShipmentNoteNumber('');
            }}
            initialStepKey={
              !previewedOutboundProcess || previewedOutboundProcess.shipmentNote
                ? 'shipment'
                : 'despatch'
            }
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        )}

      <SupplyChainProcessDrawers
        isLsp={isLsp}
        isDesktop={isDesktop}
        isTablet={isTablet}
        inboundProcess={inboundProcessState}
        outboundProcess={outboundProcessState}
        goodsReceivedNote={goodsReceivedNoteState}
        authoriseForPicking={authoriseForPickingState}
        pickingNote={pickingNoteState}
        outboundShipmentNote={outboundShipmentNoteState}
        proofOfDelivery={proofOfDeliveryState}
      />
    </>
  );
}
